import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography, Box, CircularProgress, Button, Link } from '@mui/material';
import { Facebook, Instagram, Language } from '@mui/icons-material';
import { Storage } from 'aws-amplify';
import { TranslucentContainer } from '../../../../../components';
import * as UserActions from '../../../../../core/users/action';

import BalanceCard from './BalanceCard';
import VendorProfileFormModal from './VendorProfileFormModal';
import { GROUP_USER } from '../../../../../config/appGroupConfig';
import { useIsMobile } from '../../../../../hooks';

export default function VendorProfileCard() {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const { eventList, loading } = useSelector((store) => store.vendorEvents);
  const { user, imgUpdated, loading: loadingImg } = useSelector((store) => store.users);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [logoImg, setLogoImg] = useState();
  const [logoImgUrl, setLogoImgUrl] = useState(null);

  useEffect(() => {
    if (!isFirstLoad) return;

    dispatch(UserActions.getUserByCognitoId(user.cognito_id));
    setIsFirstLoad(false);
  }, [dispatch, user, isFirstLoad]);

  useEffect(() => {
    if (!user || logoImgUrl) return;
    (async () => {
      try {
        const signedURL = await Storage.get(user.image); // get key from Storage.list
        setLogoImgUrl(signedURL);
      } catch (error) {
        console.log('🚀 ~ file: VendorProfile.jsx ~ line 34 ~ error', error);
      }
    })();
  }, [user, logoImgUrl]);

  const handleClose = () => {
    setIsEditModalOpen(false);
  };

  const handleFileUpload = (e) => {
    if (!e.target.files) {
      return;
    }
    const file = e.target.files[0];
    const { name } = file;

    console.log('file ', file);
    setLogoImg(file);
  };

  const handleUploadPhoto = async () => {
    console.log('🚀 ~ file: VendorProfile.jsx ~ line 72 ~ handleUploadPhoto ~ user', user, logoImg);
    // UPLOAD FRONT FILE
    if (logoImg) {
      try {
        const resultUpload = await Storage.put(
          `current_profile_picture_${user.cognito_id}`,
          logoImg,
          {
            metadata: { name: `${logoImg.name}` },
          },
        );
        dispatch(UserActions.editUser({ ...user, image: resultUpload.key }));
        const signedURL = await Storage.get(resultUpload.key);
        setLogoImgUrl(signedURL);
      } catch (error) {
        console.log('Error uploading frontFile: ', error);
      }
    }
  };
  const validateUrl = (url) => {
    const regex = /^(https?|http):\/\//i;
    return regex.test(url);
  };

  const logoButtonLabel = logoImg ? 'Change logo' : 'Choose a logo';

  return (
    <Grid container justifyContent="center" textAlign="center" sx={{ mb: 3 }}>
      <TranslucentContainer>
        <div
          style={{
            minHeight: '10rem',
            maxHeight: '40rem',
            scrollBehavior: 'smooth',
            margin: '1rem 0',
          }}>
          {loading ? (
            <CircularProgress />
          ) : (
            <div>
              <Grid container spacing={2} justifyContent="center">
                {user?.role?.name === GROUP_USER.VENDOR && (
                  <>
                    <Grid
                      item
                      xs={12}
                      md={5}
                      display="flex"
                      flexDirection="column"
                      alignItems="center">
                      {loadingImg ? (
                        <CircularProgress />
                      ) : (
                        <>
                          {user.image && (
                            <img
                              src={logoImgUrl}
                              alt="vendor-logo"
                              style={{
                                width: '200px',
                                height: '220px',
                              }}
                            />
                          )}
                          <Button component="label" variant="outlined" size="small" sx={{ mt: 1 }}>
                            {logoButtonLabel}
                            <input
                              hidden
                              type="file"
                              id="logo"
                              name="avatar"
                              accept="image/png, image/jpeg"
                              onChange={handleFileUpload}
                            />
                          </Button>
                          {logoImg && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                              {logoImg.name}
                            </Typography>
                          )}
                          {logoImg && (
                            <Box textAlign="center">
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{ mt: 1 }}
                                onClick={() => handleUploadPhoto()}>
                                Upload logo
                              </Button>
                            </Box>
                          )}
                        </>
                      )}
                    </Grid>
                    <Grid item xs={12} md={5} textAlign="left">
                      <Typography
                        component="div"
                        variant="h5"
                        color="#0997BA"
                        textAlign="left"
                        marginBottom="1rem">
                        Welcome, {user.vendor_company_name || user.name || 'to fayVen'}!
                      </Typography>
                      {(!user.vendor_company_name ||
                        !user.vendor_business_desc ||
                        !(user.user_tags?.length > 0)) && (
                        <Box
                          sx={{
                            border: '1px solid #0997BA',
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                            backgroundColor: 'rgba(9, 151, 186, 0.06)',
                          }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Finish your vendor profile (3 steps)
                          </Typography>
                          <Typography variant="body2">
                            Venues see this when you apply, so a complete profile gets approved
                            faster.
                          </Typography>
                          <ul style={{ margin: '0.5rem 0', paddingLeft: '1.2rem' }}>
                            <li>
                              {user.vendor_company_name
                                ? 'Done: business name and description'
                                : 'Add your business name and what you sell (Edit)'}
                            </li>
                            <li>
                              {user.user_tags?.length > 0
                                ? 'Done: product categories'
                                : 'Pick your product categories (Edit)'}
                            </li>
                            <li>
                              {user.image ? 'Done: logo' : 'Upload your logo or a product photo'}
                            </li>
                          </ul>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setIsEditModalOpen(true)}>
                            Complete my profile
                          </Button>
                        </Box>
                      )}{' '}
                      <Grid item xs={12} display="flex" flexDirection="row" gap="1rem" mb="1rem">
                        {user.vendor_social_network?.length > 0 ? (
                          <>
                            {user.vendor_social_network.find(
                              (item) => item.name === 'Facebook' && !!item.url,
                            ) != null ? (
                              <Link
                                href={
                                  user.vendor_social_network.find(
                                    (item) => item.name === 'Facebook',
                                  )?.url
                                }
                                target="_blank"
                                display="flex"
                                title="Open Facebook site"
                                alignItems="center">
                                <Facebook sx={{ color: '#FD9D7E' }} />
                              </Link>
                            ) : null}
                            {user.vendor_social_network.find(
                              (item) => item.name === 'Instagram' && !!item.url,
                            ) != null ? (
                              <Link
                                href={
                                  user.vendor_social_network.find(
                                    (item) => item.name === 'Instagram',
                                  )?.url
                                }
                                target="_blank"
                                display="flex"
                                title="Open Instagram site"
                                alignItems="center">
                                <Instagram sx={{ color: '#FD9D7E' }} />
                              </Link>
                            ) : null}
                          </>
                        ) : null}
                        {user.web_site && (
                          <Link
                            href={
                              validateUrl(user.web_site)
                                ? user.web_site
                                : `https://${user.web_site}`
                            }
                            target="_blank"
                            display="flex"
                            title="Open Website"
                            alignItems="center">
                            <Language sx={{ color: '#FD9D7E' }} />
                          </Link>
                        )}
                      </Grid>
                      <Typography variant="subtitle1">My business</Typography>
                      <Typography variant="body2" marginBottom="0.5rem" textAlign="justify">
                        {user.vendor_business_desc
                          ? user.vendor_business_desc
                          : 'Add a short description of your business (tap Edit).'}
                      </Typography>
                      <Typography variant="subtitle1">About my products</Typography>
                      <Typography variant="body2" marginBottom="0.5rem" textAlign="justify">
                        {user.vendor_product_desc
                          ? user.vendor_product_desc
                          : 'Tell venues what you sell and your price range (tap Edit).'}
                      </Typography>
                      <Typography variant="subtitle1">Category</Typography>
                      <Grid item xs={12} sx={{ display: 'flex', mt: 1 }} flexWrap="wrap">
                        {user?.user_tags &&
                          user?.user_tags.map((userTag) => (
                            <Grid
                              item
                              xs={4}
                              key={userTag?.fk_category}
                              sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Box
                                sx={{
                                  borderRadius: '17px',
                                  backgroundColor: '#0997BA',
                                  py: 1,
                                  px: 2,
                                  color: 'white',
                                  mr: 1,
                                  mb: 1,
                                }}>
                                {userTag?.category?.name}
                              </Box>
                            </Grid>
                          ))}
                      </Grid>
                      {user.city && (
                        <Typography variant="body2" marginTop="0.5rem">
                          Based in {user.city}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setIsEditModalOpen(true)}>
                        Edit
                      </Button>
                    </Grid>
                  </>
                )}
                {user?.role?.name === GROUP_USER.VENUE && (
                  <Grid item xs={12} display="flex" justifyContent="center">
                    <Box width={isMobile ? '100%' : '60%'}>
                      <BalanceCard />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </div>
          )}
        </div>
      </TranslucentContainer>
      <VendorProfileFormModal isOpen={isEditModalOpen} handleClose={handleClose} />
    </Grid>
  );
}

VendorProfileCard.propTypes = {
  // event: PropTypes.instanceOf(Object),
};

VendorProfileCard.defaultProps = {
  // event: {},
};
